import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ApiResponse, Counterparty } from '../types';

export function useCounterparties(includeInactive = false) {
  return useQuery({
    queryKey: ['counterparties', { includeInactive }],
    queryFn: async () => {
      const path = includeInactive
        ? '/api/counterparties?includeInactive=true'
        : '/api/counterparties';
      const res = await api.get<ApiResponse<Counterparty[]>>(path);
      return res.data;
    },
  });
}

type CounterpartyInput = {
  name: string;
  invoiceRegistrationNumber?: string | null;
};

export function useCreateCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CounterpartyInput) =>
      api.post<ApiResponse<Counterparty>>('/api/counterparties', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

export function useUpdateCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: CounterpartyInput & { id: string; isActive?: boolean }) =>
      api.patch<ApiResponse<Counterparty>>(`/api/counterparties/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}

export function useDeleteCounterparty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/counterparties/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });
}
